import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';

// ---------------------------------------------------------------------------
// Autocomplete AGRUPADO com opção FORMATADA (02/10/2026). Para listas de até poucas centenas de itens em que cada opção tem
// título, subtítulo e um selo à direita, organizadas em grupos com cabeçalho fixo.
//
// Por que não os autocompletes virtualizados da pasta (autocomplete.js, autocomplete-group.js): eles usam linha de altura fixa
// (36 px, uma linha, noWrap) e a lista abre na LARGURA DO CAMPO. Em campo estreito o texto corta e não cabe segunda linha nem selo.
// Aqui a lista abre com largura própria (mínimo popupMinWidth, nunca menor que o campo), cada opção tem duas linhas e o grupo
// tem cabeçalho fixo com contador. Sem virtualização: é para catálogo pequeno, não para 10 mil registros.
//
// options: [{ id, group, label, primary, secondary, badge, badgeColor, disabled }]
//   id        identificador único (comparação da opção selecionada)
//   group     nome do grupo (as opções devem vir ordenadas por grupo)
//   label     texto que fica no campo depois de escolher (e entra na pesquisa)
//   primary   1ª linha da opção (padrão: label)        secondary  2ª linha, menor (opcional)
//   badge     selo à direita (opcional)                badgeColor cor do selo (padrão cinza)
//   disabled  opção só informativa (ex.: "grupo sem itens")
// groupInfo: { [grupo]: { note, noteColor } } — texto à direita do cabeçalho do grupo (ex.: "não carregou").
// freeOption: { primary(texto), secondary } — quando informado, a lista SEMPRE termina com uma opção "usar o que digitei"
//   (não some com o filtro). Ao escolher, onChange recebe { id: FREE_OPTION_ID, inputValue: 'texto digitado' }.
// value: a opção selecionada (mesmo id) ou null.  onChange(opção | null).
// ---------------------------------------------------------------------------

export const FREE_OPTION_ID = '__free__';

const defaultFilter = createFilterOptions({
  ignoreAccents: true,
  ignoreCase: true,
  stringify: (o) => `${o.label || ''} ${o.primary || ''} ${o.secondary || ''} ${o.group || ''} ${o.badge || ''}`,
});

export default function GroupedRichAutoComplete(props) {
  const {
    label, options, value, onChange, groupInfo, freeOption, placeholder, size, disabled, loading,
    noOptionsText, popupMinWidth, listMaxHeight, helperText, freeGroupLabel,
  } = props;

  // Lista com largura própria: pelo menos popupMinWidth e nunca menor que o campo; limitada à janela.
  const PopperComponent = React.useMemo(() => function WidePopper(popperProps) {
    const anchorWidth = popperProps.anchorEl && popperProps.anchorEl.clientWidth ? popperProps.anchorEl.clientWidth : 0;
    const width = Math.max(anchorWidth, popupMinWidth || 0);
    return <Popper {...popperProps} placement="bottom-start" style={{ ...(popperProps.style || {}), width, maxWidth: 'calc(100vw - 32px)', zIndex: 1400 }} />;
  }, [popupMinWidth]);

  const filterOptions = (list, state) => {
    const typed = String(state.inputValue || '').trim();
    const filtered = defaultFilter(list.filter((o) => o.id !== FREE_OPTION_ID), state);
    // Grupo que ficou só com a opção informativa desabilitada some do resultado filtrado quando há texto digitado.
    const cleaned = typed ? filtered.filter((o) => !o.disabled) : filtered;
    if (freeOption) cleaned.push({ id: FREE_OPTION_ID, group: freeGroupLabel || ' ', label: typed, inputValue: typed, primary: freeOption.primary(typed), secondary: freeOption.secondary });
    return cleaned;
  };

  return (
    <Autocomplete
      fullWidth
      size={size || 'small'}
      options={options}
      value={value || null}
      disabled={disabled}
      loading={loading}
      autoHighlight
      openOnFocus
      selectOnFocus
      handleHomeEndKeys
      PopperComponent={PopperComponent}
      filterOptions={filterOptions}
      groupBy={(o) => o.group || ''}
      getOptionLabel={(o) => (o && o.label) || ''}
      getOptionDisabled={(o) => !!o.disabled}
      isOptionEqualToValue={(o, v) => !!v && o.id === v.id}
      noOptionsText={noOptionsText || 'Nada encontrado'}
      ListboxProps={{ style: { maxHeight: listMaxHeight || 420, padding: 0 } }}
      onChange={(event, option) => onChange(option || null)}
      renderGroup={(params) => {
        const info = (groupInfo && groupInfo[params.group]) || null;
        const count = React.Children.count(params.children);
        return (
          <li key={params.key}>
            <Box style={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 14px', background: '#f5f7f9', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
              <Typography style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: '#455a64', textTransform: 'uppercase' }}>{params.group}</Typography>
              <Typography style={{ fontSize: 11, color: (info && info.noteColor) || '#78909c', whiteSpace: 'nowrap' }}>{info && info.note ? info.note : count}</Typography>
            </Box>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        );
      }}
      renderOption={(optionProps, o) => {
        const isFree = o.id === FREE_OPTION_ID;
        return (
          <li {...optionProps} key={o.id + (isFree ? o.inputValue || '' : '')} style={{ ...(optionProps.style || {}), padding: '7px 14px', alignItems: 'center', gap: 12, borderTop: isFree ? '1px dashed #cfd8dc' : undefined }}>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography style={{ fontSize: 13.5, fontWeight: isFree ? 600 : 500, color: isFree ? '#277afe' : o.disabled ? '#9e9e9e' : '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: o.disabled ? 'italic' : 'normal' }}>{o.primary || o.label}</Typography>
              {o.secondary && <Typography style={{ fontSize: 11.5, color: '#78909c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.secondary}</Typography>}
            </Box>
            {o.badge && (
              <span style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', color: o.badgeColor || '#455a64', background: '#eceff1', border: '1px solid #cfd8dc' }}>{o.badge}</span>
            )}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} variant="outlined" placeholder={placeholder} helperText={helperText} InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{ ...params.InputProps, endAdornment: (<>{loading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
      )}
    />
  );
}

GroupedRichAutoComplete.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  groupInfo: PropTypes.object,
  freeOption: PropTypes.object,
  placeholder: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  noOptionsText: PropTypes.string,
  popupMinWidth: PropTypes.number,
  listMaxHeight: PropTypes.number,
  helperText: PropTypes.node,
  freeGroupLabel: PropTypes.string,
};
